import {
  types as t,
  getParent,
  flow,
  getEnv,
  getSnapshot,
  type Instance,
} from 'mobx-state-tree';
import ModelVersion, { type IModelVersion } from './model_version';
import type { IProject } from './project';
import type { IStoreEnv } from '../env';
import { DateTime } from 'luxon';

type Notation = 'MERODE' | 'UML';
type SaveState = 'idle' | 'error' | 'busy';

/**
 * Hand-declared instance interface for ModelInformation. It sits on the projects
 * recursion boundary (`project` reaches up via `getParent`, `versions` reach back
 * down), so per the MST typing pattern (§3) the interface is declared by hand and
 * used to rebind `self` in the views block (§4).
 */
export interface IModelInformation {
  readonly id: string;
  readonly name: string;
  readonly notation: Notation;
  readonly generateMethods: boolean;
  readonly propagateOnDependency: boolean;
  readonly propagateOnMethod: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly versions: IModelVersion[];
  readonly saveState: SaveState;
  readonly project: IProject;
  readonly updated: DateTime;
  readonly activeVersion: IModelVersion | undefined;
  readonly lastEdited: DateTime;
  readonly combinedSaveState: SaveState | undefined;
  readonly history: IModelVersion[];
  findVersionById(id: string): IModelVersion | undefined;
  changeSettings(
    name: string,
    notation: Notation,
    generateMethods: boolean,
    propagateOnDependency: boolean,
    propagateOnMethod: boolean
  ): Promise<void>;
  saveSettings(): Promise<boolean>;
  retrySave(): Promise<unknown>;
  saveVersion(name: string): Promise<void>;
  restoreVersion(id: string): Promise<void>;
}

/**
 * A Model (see `ARCHITECTURE.md`).
 *
 * Named ModelInformation here to avoid a name clash with the MST model defined in `../model`.
 */
const ModelInformation = t
  .model('ModelInformation', {
    id: t.string,
    name: t.string,
    notation: t.optional(t.enumeration(['MERODE', 'UML']), 'MERODE'),
    generateMethods: true,
    propagateOnDependency: true,
    propagateOnMethod: true,
    createdAt: t.string,
    updatedAt: t.string,
    versions: t.optional(t.array(ModelVersion), []),
    // keep track of the save operation, is it idle, busy or did it error?
    saveState: t.optional(t.enumeration(['idle', 'error', 'busy']), 'idle'),
  })
  .views(self0 => {
    // Rebind `self` to the hand-declared interface so the sibling views below
    // (`self.updated`, `self.activeVersion`, …) type — same observable node at
    // runtime (§4). `getParent` still receives the real node `self0` (§4).
    const self = self0 as unknown as IModelInformation;
    return {
    // convenience method to get the project
    get project(): IProject {
        // 2 levels up because 1 level up in the tree is the array of models
        return getParent<IProject>(self0, 2);
    },
    // luxon DateTime object for the updatedAt property
    get updated() {
      return DateTime.fromISO(self.updatedAt);
    },
    // get the active version
    get activeVersion() {
      return self.versions.find(version => version.active);
    },
    // lastEdited = maximum of our own updatedAt and the updatedAt of the active
    // version.
    get lastEdited() {
      // Legacy behaviour assumes an active version exists when `lastEdited` is
      // read; the interface types `activeVersion` as possibly-undefined. Assert
      // non-null to keep byte-identical runtime without adding a guard.
      return DateTime.max(self.updated, self.activeVersion!.updated);
    },
    // Combine our own save state with the save state of the active version
    get combinedSaveState(): SaveState | undefined {
      if (self.saveState === 'idle') {
        return self.activeVersion!.saveState;
      } else if (self.saveState === 'busy') {
        return self.saveState;
      } else if (self.saveState === 'error') {
        if (self.activeVersion!.saveState === 'busy') {
          return self.activeVersion!.saveState;
        } else {
          return self.saveState;
        }
      }
    },
    // Get the non-active versions sorted by createdAt (descending)
    get history() {
      return self.versions
        .filter(v => !v.active)
        .sort((one, two) => Date.parse(two.createdAt) - Date.parse(one.createdAt));
    },
    findVersionById(id: string) {
      return self.versions.find(v => v.id === id);
    },
    };
  })
  .actions(self0 => {
    // `self0` is the raw MST node (mutable props, visible views). `self` is the
    // hand-declared interface alias used only to read sibling *actions*
    // (`saveSettings`) defined in this same block (§4); property writes stay on
    // `self0`.
    const self = self0 as unknown as IModelInformation;
    return {
      /**
       * Change our settings and save them to the backend.
       */
      changeSettings: flow(function* changeSettings(
        name: string,
        notation: Notation,
        generateMethods: boolean,
        propagateOnDependency: boolean,
        propagateOnMethod: boolean
      ) {
        self0.name = name;
        self0.notation = notation;
        self0.generateMethods = generateMethods;
        self0.propagateOnDependency = propagateOnDependency;
        self0.propagateOnMethod = propagateOnMethod;
        yield self.saveSettings();
      }),
      /**
       * Save our settings to the backend
       */
      saveSettings: flow(function* saveSettings() {
        self0.saveState = 'busy';
        try {
          const api = getEnv<IStoreEnv>(self0).api;
          const json: { updatedAt: string } = yield api.projects.updateModelSettings(
            self.project.id,
            self.id,
            {
              name: self.name,
              notation: self.notation,
              generateMethods: self.generateMethods,
              propagateOnDependency: self.propagateOnDependency,
              propagateOnMethod: self.propagateOnMethod,
            }
          );
          self0.updatedAt = json.updatedAt;
          self0.saveState = 'idle';
          return true;
        } catch (err) {
          self0.saveState = 'error';
          return false;
        }
      }),
      /**
       * Retry saving.
       *
       * Combines retry saving of the active version with retry saving our own
       * settings if needed.
       */
      retrySave() {
        // Legacy behaviour: an active version is present when retrySave runs.
        const promises = [self.activeVersion!.retrySave()];
        if (self.saveState === 'error') {
          promises.push(self.saveSettings());
        }
        return Promise.all(promises);
      },
      /**
       * Save a new version with the given name.
       *
       * The content for the new version is set to a snapshot of the active
       * version.
       */
      saveVersion: flow(function* saveVersion(name: string) {
        const api = getEnv<IStoreEnv>(self0).api;
        const version = {
          name,
          content: getSnapshot(self.activeVersion!.content!),
        };
        const json = yield api.projects.createModelVersion(
          self.project.id,
          self.id,
          version
        );
        self0.versions.push(json);
      }),
      /**
       * Restore the version with the given id.
       *
       * First loads the content of the version if it is not yet available
       * locally. Then sets the content of the active version to a snapshot of
       * the content of version to restore.
       */
      restoreVersion: flow(function* restoreVersion(id: string) {
        const version = self.versions.find(v => v.id === id && !v.active);
        if (version) {
          if (!version.contentAvailable) {
            yield version.loadContent();
          }
          // MST accepts a snapshot for a `content` model assignment at runtime;
          // the instance write type doesn't include the snapshot shape, and the
          // `activeVersion` view types `content` as readonly, so cast the target
          // to the writable ModelVersion instance at this single assignment
          // boundary (documented MST gap).
          const active = self.activeVersion! as unknown as Instance<typeof ModelVersion>;
          active.content = getSnapshot(version.content!) as unknown as typeof active.content;
        } else {
          throw new Error('Version not found');
        }
      }),
    };
  });

export default ModelInformation;
